import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewEncapsulation, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwElementsBaseUrlsModel } from '../../shared/_models/gtw-elements-base-urls.model';
import { HttpClient } from "@angular/common/http";
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { taxYears, returnTypes } from "./dataStub";
import { UploadRequest } from "../models/upload-irs.model";
import { Subscription } from 'rxjs';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import { generalConfigFromGlobal } from '../../shared/workspace-element-context';

@Component({
    selector: 'efile-upload-irs',
    templateUrl: './upload-irs.component.html',
    styleUrls: ['./upload-irs.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UploadIRSComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  returnTypes = returnTypes;
  taxYears = taxYears;
  baseURLs: GtwElementsBaseUrlsModel = {
    api: '/gtw',
    custom: '/custom',
    admin: '/admin',
    efile: '/gtw-efile-api',
    pdfEngine: '/pdf-engine',
    bulkPdfEngine: '/bulk-pdf-engine'
  };

  uploading: boolean = false;
  minimise: boolean = false;
  fileUploadConfig: any = {
    multiple: false,
    type: 'inline',
    trigger: false,
    accept: '.zip',
  };
  selectedFile: File | null = null;

  taxYearOnModal: string = '2020';
  returnTypeOnModal: string = '7004';
  uploadParams: UploadRequest = {
    filingYear: this.taxYearOnModal,
    formType: this.returnTypeOnModal,
    schemaVersion: undefined,
    schemaZipFile: ''
  };
  uploadUrl: string = '';

  clientKey: number = 0;

  constructor(
    private httpClient: HttpClient,
    private snackbarService: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    this.uploadUrl = `${this.baseURLs.efile}/loadSchema`;
    this.clientKey = Number(this.gtwApp.USER_SETTINGS?.user?.client_key) || 0;
  }

  closeModal(emitCloseEvent: boolean = true) {
    if (emitCloseEvent) {
      this.requestGridUpdate.emit('cancel');
    }
  }

  onFileAdded(event: any) {
    this.selectedFile = event?._file instanceof File ? event._file : null;
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files && input.files.length > 0 ? input.files[0] : null;
    this.selectedFile = file instanceof File ? file : null;
  }

  uploadFiles(event: any) {
    let file: File | null = null;

    if (Array.isArray(event) && event.length > 0) {
      const fileItem = event[event.length - 1];
      file = fileItem?._file instanceof File ? fileItem._file : null;
    }

    if (!file && this.selectedFile instanceof File) {
      file = this.selectedFile;
    }

    if (!file) return;
    this.doUpload(file);
  }

  manualUpload(): void {
    if (!this.selectedFile || this.uploading) return;
    this.doUpload(this.selectedFile);
  }

  private doUpload(file: File): void {
    this.uploading = true;

    const formData = new FormData();
    formData.append('file', file, file.name);

    let url = this.uploadUrl
      + '?filingYear=' + encodeURIComponent(this.uploadParams.filingYear || '')
      + '&formType=' + encodeURIComponent(this.uploadParams.formType || '')
      + '&schemaVersion=' + encodeURIComponent(this.uploadParams.schemaVersion?.toString() || '')
      + '&schemaZipFile=' + encodeURIComponent(file.name);

    this.httpClient.post<any>(url, formData, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.uploading = false;
        if (response?.callSuccess === '1') {
          this.selectedFile = null;
          this.fileUploadConfig = { ...this.fileUploadConfig };
          this.snackbarService.openSnackBar('Schema uploaded successfully.', 'gtw-snackbar--green');
          this.closeModal(false);
          this.requestGridUpdate.emit('refresh');
        } else {
          const errorMsg = response?.errorMessage || 'Upload failed. Please try again.';
          this.snackbarService.openSnackBar(errorMsg, 'gtw-snackbar--red');
        }
      },
      (error: any) => {
        this.uploading = false;
        console.error('Schema upload failed:', error);
        this.snackbarService.openSnackBar('Upload failed. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  modalTaxYearSelected(selectedValue: string): void {
    this.taxYearOnModal = selectedValue;
    this.uploadParams.filingYear = selectedValue;
  }

  returnTypeSelected(selectedValue: string): void {
    this.returnTypeOnModal = selectedValue;
    this.uploadParams.formType = selectedValue;
  }
}
